/**
 * Sync Engine — singleton orchestrator.
 *
 * Ties together: NetworkHealth events, SyncQueue processing, UI reactivity.
 * Not bound to any Vue component lifecycle — runs for the entire app session.
 *
 * Responsibilities:
 *   - On 'recovered': flush the entire sync queue
 *   - On 'visibilitychange' (tab visible): check queue and sync
 *   - Periodic 30s poll when online and queue is non-empty
 *   - Exposes reactive state for UI (pendingCount, isSyncing, lastSyncAt)
 */
import { ref, readonly } from 'vue'
import { processQueue, getPendingCount } from './syncQueue'
import { logError, flushErrorLogs } from './errorLogger'

let _started = false
let _apiClient = null
let _unsubNetwork = null
let _pollTimer = null

const POLL_INTERVAL = 30000

const isSyncing  = ref(false)
const pendingCount = ref(0)
const lastSyncAt = ref(null)
const lastError  = ref(null)

async function _refreshCount() {
  try {
    pendingCount.value = await getPendingCount()
  } catch { /* IndexedDB might not be ready yet */ }
}

async function _doSync() {
  if (isSyncing.value || !_apiClient) return
  const count = await getPendingCount()
  if (count === 0) {
    pendingCount.value = 0
    return
  }

  isSyncing.value = true
  lastError.value = null
  console.log('[SyncEngine] Starting sync, queue size:', count)

  try {
    const result = await processQueue(_apiClient)
    lastSyncAt.value = new Date().toISOString()
    console.log('[SyncEngine] Sync complete:', result)
    if (result.failed > 0) {
      logError('sync_error', `Sync completed with ${result.failed} failed items`, {
        action: 'processQueue',
        extra: result,
      })
    }
    // Flush error logs after successful sync
    flushErrorLogs()
  } catch (err) {
    lastError.value = err?.message || 'Sync failed'
    console.error('[SyncEngine] Sync error:', err)
    logError('sync_error', err, { action: 'processQueue' })
  } finally {
    isSyncing.value = false
    await _refreshCount()
    // Report the current offline-media snapshot to the server (audit/office
    // visibility). Dynamic import avoids a static circular dependency with
    // offlineApi (which imports notifyEnqueued from here). Best-effort.
    import('./offlineApi').then(m => m.reportPendingMedia?.()).catch(() => {})
  }
}

function _schedulePoll() {
  if (_pollTimer) clearInterval(_pollTimer)
  _pollTimer = setInterval(async () => {
    const count = await getPendingCount()
    pendingCount.value = count
    if (count > 0) _doSync()
  }, POLL_INTERVAL)
}

/**
 * Initialize the sync engine. Call once from main.js AFTER network health is ready.
 */
export async function initSyncEngine(apiClient) {
  if (_started) return
  _started = true
  _apiClient = apiClient

  await _refreshCount()

  // Subscribe to network health events (recovered → immediate sync)
  try {
    const { useNetworkHealth } = await import('@/composables/useNetworkHealth')
    const { onNetworkEvent } = useNetworkHealth()
    _unsubNetwork = onNetworkEvent((event) => {
      if (event === 'recovered') {
        console.log('[SyncEngine] Network recovered — flushing queue')
        _doSync()
      }
    })
  } catch (e) {
    console.warn('[SyncEngine] Could not subscribe to network events:', e.message)
  }

  // Sync when tab becomes visible
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) _refreshCount().then(() => { if (pendingCount.value > 0) _doSync() })
  })

  // Native: iOS has no background Service Worker sync in Capacitor, so the
  // reliable moment to flush the queue is when the app returns to the
  // foreground. @capacitor/app fires `appStateChange` with isActive=true; that
  // is our cue to drain anything queued while the app was backgrounded/offline.
  // (visibilitychange also fires in most WebViews, but appStateChange is the
  // documented, dependable native signal — both are idempotent here.)
  _initAppLifecycle()

  // Periodic poll
  _schedulePoll()

  // Initial sync if items are pending
  if (pendingCount.value > 0) _doSync()

  // Push the current offline-media snapshot to the server on boot so stale rows
  // from a previous session are cleared even when nothing is pending now.
  import('./offlineApi').then(m => m.reportPendingMedia?.()).catch(() => {})

  console.log('[SyncEngine] Initialized, pending:', pendingCount.value)
}

/**
 * Wire @capacitor/app foreground events to a queue flush (native only).
 * Best-effort and lazy-loaded so the web bundle never pays for it.
 */
async function _initAppLifecycle() {
  try {
    const { isNative } = await import('@/native/platform')
    if (!isNative()) return
    const { App } = await import('@capacitor/app')
    App.addListener('appStateChange', (state) => {
      if (state?.isActive) {
        console.log('[SyncEngine] App foregrounded — checking queue')
        _refreshCount().then(() => { if (pendingCount.value > 0) _doSync() })
      }
    })
  } catch (e) {
    console.warn('[SyncEngine] App lifecycle wiring skipped:', e?.message || e)
  }
}

/**
 * Manually trigger sync (e.g. from "Sync" button in UI).
 */
export async function triggerSync() {
  await _doSync()
}

/**
 * Notify the engine that a new item was enqueued (updates pending count).
 */
export async function notifyEnqueued() {
  await _refreshCount()
}

/**
 * Composable for Vue components to read sync state.
 */
export function useSyncEngine() {
  return {
    isSyncing:    readonly(isSyncing),
    pendingCount: readonly(pendingCount),
    lastSyncAt:   readonly(lastSyncAt),
    lastError:    readonly(lastError),
    triggerSync,
    notifyEnqueued,
  }
}
